import React, { useState } from 'react'
import { Refresh as RefreshIcon } from '@mui/icons-material'
import { Button, CircularProgress, Tooltip } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import SimOpsAPI from 'api/ops/v2/SimOpsAPI'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import format from 'date-fns/format'
import {
  PackAction,
  PackAllocation,
  PackAssignmentStatus
} from 'types/api/ops/v2/simOps.type'

import PackActionDialog from '../dialogs/PackActionDialog'
import { USER_ALLOCATIONS_QUERY_KEY } from '../simOpsQueries'

import {
  CellText,
  fitTableHeight,
  formatDataInGB,
  formatValidityDays,
  listGridProps,
  ListSection,
  MonoText,
  MutedText,
  RowActionsMenu,
  StatusPill
} from './listUi'

// replay is rejected by the backend once a pack is ASSIGNED ("use reset instead")
const getAvailableActions = (status: PackAssignmentStatus): PackAction[] => {
  if (status === 'TERMINATED') return []
  return status === 'ASSIGNED' ? ['reset', 'terminate'] : ['replay', 'terminate']
}

const ACTION_LABEL: Record<PackAction, string> = {
  replay: 'Replay',
  reset: 'Reset',
  terminate: 'Terminate'
}

const formatDate = (value?: string) =>
  value ? format(new Date(value), 'dd MMM yyyy, HH:mm') : '—'

const packsAllocationColumns = (
  onAction: (row: PackAllocation, action: PackAction) => void
) => [
  {
    field: 'catalog_name',
    headerName: 'Pack',
    sortable: false,
    flex: 1.2,
    minWidth: 170,
    renderCell: (params: { row: PackAllocation }) => (
      <Tooltip title={`Pack assignment ID: ${params.row.id}`}>
        <span>
          <CellText strong>
            {params.row.catalogName || params.row.catalog_name || '—'}
          </CellText>
        </span>
      </Tooltip>
    )
  },
  {
    field: 'dataInGB',
    headerName: 'Data',
    sortable: false,
    flex: 0.6,
    minWidth: 90,
    renderCell: (params: { row: PackAllocation }) => (
      <CellText>{formatDataInGB(params.row.dataInGB)}</CellText>
    )
  },
  {
    field: 'validityInDays',
    headerName: 'Validity',
    sortable: false,
    flex: 0.6,
    minWidth: 90,
    renderCell: (params: { row: PackAllocation }) => (
      <CellText>{formatValidityDays(params.row.validityInDays)}</CellText>
    )
  },
  {
    field: 'status',
    headerName: 'Status',
    sortable: false,
    flex: 0.7,
    minWidth: 120,
    renderCell: (params: { row: PackAllocation }) => (
      <StatusPill status={params.row.status} />
    )
  },
  {
    field: 'iccid',
    headerName: 'ICCID',
    sortable: false,
    flex: 1.1,
    minWidth: 180,
    renderCell: (params: { row: PackAllocation }) => (
      <MonoText>{params.row.iccid}</MonoText>
    )
  },
  {
    field: 'receiver_email',
    headerName: 'Allocated To',
    sortable: false,
    flex: 1,
    minWidth: 150,
    renderCell: (params: { row: PackAllocation }) => (
      <CellText>{params.row.receiver_email || '—'}</CellText>
    )
  },
  {
    field: 'created_at',
    headerName: 'Purchased',
    sortable: false,
    flex: 0.9,
    minWidth: 140,
    renderCell: (params: { row: PackAllocation }) => (
      <MutedText>{formatDate(params.row.created_at)}</MutedText>
    )
  },
  {
    field: 'actions',
    headerName: '',
    sortable: false,
    flex: 0,
    width: 56,
    align: 'right' as const,
    renderCell: (params: { row: PackAllocation }) => (
      <RowActionsMenu
        actions={getAvailableActions(params.row.status).map((action) => ({
          label: ACTION_LABEL[action],
          danger: action === 'terminate',
          onClick: () => onAction(params.row, action)
        }))}
      />
    )
  }
]

function PacksAllocationCard({ userId }: { userId: string }) {
  const [gridRefreshKey, setGridRefreshKey] = useState(0)
  const [selectedPack, setSelectedPack] = useState<PackAllocation | null>(null)
  const [selectedAction, setSelectedAction] = useState<PackAction | null>(null)
  const pageSize = 10
  const [page, setPage] = useState(0)

  const {
    data: customerPacks,
    isLoading,
    isFetching,
    refetch: refetchAllocations
  } = useQuery(
    [USER_ALLOCATIONS_QUERY_KEY, userId, page, pageSize],
    async () => {
      const { data } = await SimOpsAPI.getUserAllocations({
        userId,
        limit: pageSize,
        offset: page * pageSize
      })
      return data
    },
    { keepPreviousData: true, enabled: !!userId }
  )

  const visibleRows = customerPacks?.body?.data
    ? customerPacks.body.data.filter(
        (pack) => !['ASSIGNED', 'INACTIVE', 'TERMINATED'].includes(pack.status)
      )
    : []

  const hiddenTerminatedCount = (customerPacks?.body?.data?.length || 0) - visibleRows.length
  const rowCount = Math.max((customerPacks?.body?.total || 0) - hiddenTerminatedCount, 0)

  const sortedRows = visibleRows.length
    ? [...visibleRows].sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )
    : []

  const handleAction = (pack: PackAllocation, action: PackAction) => {
    setSelectedPack(pack)
    setSelectedAction(action)
  }

  const handleDialogClose = () => {
    setSelectedPack(null)
    setSelectedAction(null)
  }

  const handleActionSuccess = () => {
    if (selectedAction === 'replay') {
      setGridRefreshKey((currentKey) => currentKey + 1)
    }
  }

  return (
    <>
      <ListSection
        title='Data Pack Allocations'
        description='Allocations not yet assigned, including failed ones.'
        actions={
          <Button
            variant='outlined'
            size='small'
            color='inherit'
            startIcon={
              isFetching ? (
                <CircularProgress size={14} color='inherit' />
              ) : (
                <RefreshIcon fontSize='small' />
              )
            }
            onClick={() => refetchAllocations()}
            disabled={isFetching}
            sx={{
              borderColor: '#D0D5DD',
              color: '#344054',
              textTransform: 'none',
              borderRadius: '8px',
              fontWeight: 500
            }}
          >
            {isFetching ? 'Reloading' : 'Reload'}
          </Button>
        }
      >
        <MUIDataGrid
          {...listGridProps}
          key={gridRefreshKey}
          columns={packsAllocationColumns(handleAction)}
          rows={sortedRows}
          getRowId={(row: PackAllocation) => row.id}
          tableHeight={fitTableHeight(sortedRows.length, {
            maxRows: pageSize,
            footer: true
          })}
          pageCount={Math.ceil(rowCount / pageSize)}
          pageNumber={page + 1}
          onPageChange={(newPageNumber) => setPage(newPageNumber - 1)}
          disableColumnMenu
          loading={isLoading || isFetching}
        />
      </ListSection>
      <PackActionDialog
        userId={userId}
        action={selectedAction}
        pack={selectedPack}
        handleClose={handleDialogClose}
        onActionSuccess={handleActionSuccess}
      />
    </>
  )
}

export default PacksAllocationCard
